import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Language as LanguageIcon,
  ExpandMore,
  Check
} from '@mui/icons-material';
import { useLanguage } from '../../localization';
import tokens from '../../theme/tokens';

const LanguageSwitcher = ({ 
  variant = 'button', // 'button' | 'compact' | 'minimal'
  size = 'medium', // 'small' | 'medium' | 'large'
  showLabel = true,
  position = 'right' // 'left' | 'right' | 'center'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentLanguage, availableLanguages, changeLanguage, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode) => {
    await changeLanguage(languageCode);
    handleClose();
  };

  const currentLang = availableLanguages[currentLanguage];

  // Compact version for mobile or minimal space
  if (variant === 'compact' || (isMobile && variant !== 'minimal')) {
    return (
      <Box>
        <Tooltip title={t('language.select', 'Language')}>
          <Button
            onClick={handleClick}
            size={size}
            sx={{
              minWidth: 'auto',
              p: 1,
              borderRadius: tokens.radius.base,
              color: tokens.colors.text.secondary,
              '&:hover': {
                backgroundColor: tokens.colors.alpha.black[50],
                color: tokens.colors.text.primary
              }
            }}
          >
            <Box sx={{ fontSize: '1.2rem', mr: showLabel ? 1 : 0 }}>
              {currentLang?.flag}
            </Box>
            {showLabel && !isMobile && (
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {currentLang?.code?.toUpperCase()}
              </Typography>
            )}
            <ExpandMore sx={{ fontSize: 16, ml: 0.5 }} />
          </Button>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: position
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: position
          }}
          PaperProps={{
            sx: {
              borderRadius: tokens.radius.lg,
              boxShadow: tokens.shadows.lg,
              border: `1px solid ${tokens.colors.borders.light}`,
              minWidth: 160
            }
          }}
        >
          {Object.values(availableLanguages).map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              selected={currentLanguage === language.code}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: `${tokens.colors.primary.main}10`,
                  '&:hover': {
                    backgroundColor: `${tokens.colors.primary.main}20`
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ fontSize: '1.2rem' }}>{language.flag}</Box>
              </ListItemIcon>
              <ListItemText
                primary={language.nativeName}
                secondary={language.name !== language.nativeName ? language.name : null}
                primaryTypographyProps={{
                  fontWeight: currentLanguage === language.code ? tokens.typography.fontWeights.semibold : tokens.typography.fontWeights.regular,
                  fontSize: '0.875rem'
                }}
                secondaryTypographyProps={{
                  fontSize: '0.75rem'
                }}
              />
              {currentLanguage === language.code && (
                <Check
                  sx={{
                    color: tokens.colors.primary.main,
                    fontSize: 18,
                    ml: 1
                  }}
                />
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  // Minimal version - just the flag
  if (variant === 'minimal') {
    return (
      <Box>
        <Tooltip title={`${currentLang?.nativeName} - ${t('language.select', 'Language')}`}>
          <Button
            onClick={handleClick}
            size={size}
            sx={{
              minWidth: 'auto',
              p: 1,
              borderRadius: '50%',
              color: tokens.colors.text.secondary,
              '&:hover': {
                backgroundColor: tokens.colors.alpha.black[50]
              }
            }}
          >
            <Box sx={{ fontSize: '1.2rem' }}>
              {currentLang?.flag}
            </Box>
          </Button>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: position
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: position
          }}
          PaperProps={{
            sx: {
              borderRadius: tokens.radius.lg,
              boxShadow: tokens.shadows.lg,
              border: `1px solid ${tokens.colors.borders.light}`,
              minWidth: 160
            }
          }}
        >
          {Object.values(availableLanguages).map((language) => (
            <MenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              selected={currentLanguage === language.code}
              sx={{
                py: 1.5,
                px: 2,
                '&.Mui-selected': {
                  backgroundColor: `${tokens.colors.primary.main}10`
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ fontSize: '1.2rem' }}>{language.flag}</Box>
              </ListItemIcon>
              <ListItemText
                primary={language.nativeName}
                primaryTypographyProps={{
                  fontWeight: currentLanguage === language.code ? tokens.typography.fontWeights.semibold : tokens.typography.fontWeights.regular,
                  fontSize: '0.875rem'
                }}
              />
              {currentLanguage === language.code && (
                <Check
                  sx={{
                    color: tokens.colors.primary.main,
                    fontSize: 18,
                    ml: 1
                  }}
                />
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  // Default button version
  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={<LanguageIcon />}
        endIcon={<ExpandMore />}
        size={size}
        sx={{
          color: tokens.colors.text.secondary,
          textTransform: 'none',
          fontWeight: tokens.typography.fontWeights.medium,
          borderRadius: tokens.radius.base,
          px: 2,
          py: 1,
          '&:hover': {
            backgroundColor: tokens.colors.alpha.black[50],
            color: tokens.colors.text.primary
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: '1.1rem' }}>{currentLang?.flag}</Box>
          {showLabel && (
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {currentLang?.nativeName}
            </Typography>
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: position
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: position
        }}
        PaperProps={{
          sx: {
            borderRadius: tokens.radius.lg,
            boxShadow: tokens.shadows.lg,
            border: `1px solid ${tokens.colors.borders.light}`,
            minWidth: 200
          }
        }}
      >
        {Object.values(availableLanguages).map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={currentLanguage === language.code}
            sx={{
              py: 1.5,
              px: 2,
              '&.Mui-selected': {
                backgroundColor: `${tokens.colors.primary.main}10`,
                '&:hover': {
                  backgroundColor: `${tokens.colors.primary.main}20`
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Box sx={{ fontSize: '1.3rem' }}>{language.flag}</Box>
            </ListItemIcon>
            <ListItemText
              primary={language.nativeName}
              secondary={language.name !== language.nativeName ? language.name : null}
              primaryTypographyProps={{
                fontWeight: currentLanguage === language.code ? tokens.typography.fontWeights.semibold : tokens.typography.fontWeights.regular
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem'
              }}
            />
            {currentLanguage === language.code && (
              <Check
                sx={{
                  color: tokens.colors.primary.main,
                  fontSize: 20,
                  ml: 1
                }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher; 